<template>
    <v-card outlined style="display:container">
        <v-card-text>
            <!-- Search Autocomplete -->
            <v-autocomplete
                v-model="selectedPlace"
                :items="searchResults"
                :loading="isSearching"
                :search-input.sync="searchQuery"
                item-text="display_name"
                item-value="place_id"
                :label="$t('SearchLocation') || 'Search location...'"
                prepend-inner-icon="mdi-magnify"
                return-object
                clearable
                hide-no-data
                no-filter
                outlined
                dense
                class="mb-2"
                @change="onPlaceSelected"
            >
                <template v-slot:item="{ item }">
                    <v-list-item-content>
                        <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                    </v-list-item-content>
                </template>
            </v-autocomplete>

            <!-- Map Container -->
            <div class="map-wrapper">
                <div id='map' ref='map' style='height: 400px;width:100%;overflow:hidden'></div>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
    import L from 'leaflet'
    import 'leaflet/dist/leaflet.css'

    export default {
        name: 'LocationPicker',

        props: {
            value: {
                type: Object,
                default: () => ({ lat: null, lng: null })
            }
        },

        data() {
            return {
                map: null,
                marker: null,
                lat: this.value.lat,
                lng: this.value.lng,
                // Search functionality
                searchQuery: '',
                searchResults: [],
                selectedPlace: null,
                isSearching: false,
                searchTimeout: null,
            }
        },

        watch: {
            searchQuery(val) {
                // Debounce search
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout)
                }
                
                if (!val || val.length < 3) {
                    this.searchResults = []
                    return
                }

                this.searchTimeout = setTimeout(() => {
                    this.searchLocation(val)
                }, 400)
            }
        },

        mounted() {
            this.$nextTick(() => {
                this.initMap()
            })
        },

        beforeDestroy() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            if (this.map) {
                this.map.remove()
                this.map = null
            }
        },

        methods: {
            initMap() {
                this.map = L.map(this.$refs.map).setView(
                    [this.lat || 25.4147766, this.lng || 42.1625726],
                    12
                )

                // Satellite layer (Esri World Imagery)
                const satelliteLayer = L.tileLayer(
                    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                    {
                        attribution: ''
                    }
                )

                // Street layer (OpenStreetMap)
                const streetLayer = L.tileLayer(
                    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                    {
                        attribution: ''
                    }
                )

                // Add street layer by default
                streetLayer.addTo(this.map)

                // Add layer control to switch between views
                const baseMaps = {
                    'Street': streetLayer,
                    'Satellite': satelliteLayer
                }
                L.control.layers(baseMaps, null, { position: 'topright' }).addTo(this.map)

                if (this.lat && this.lng) {
                    this.addMarker({ lat: this.lat, lng: this.lng })
                }

                this.map.on('click', this.onMapClick)
                
                // Delay invalidateSize to ensure container is fully rendered
                setTimeout(() => {
                    if (this.map) {
                        this.map.invalidateSize()
                    }
                }, 100)
            },

            onMapClick(e) {
                this.addMarker(e.latlng)

                this.lat = e.latlng.lat
                this.lng = e.latlng.lng

                this.$emit('input', {
                    lat: this.lat,
                    lng: this.lng
                })
            },

            addMarker(latlng) {
                var customIcon = L.icon({
                    iconUrl: '/marker.png',        // your image
                    iconSize: [40, 40],            // width, height
                    iconAnchor: [20, 40],          // point of the icon which corresponds to marker location
                    popupAnchor: [0, -40]
                })
                if (this.marker) {
                    this.marker.setLatLng(latlng)
                } else {
                    this.marker = L.marker(latlng, { draggable: true, icon: customIcon }).addTo(this.map)

                    this.marker.on('dragend', e => {
                        const pos = e.target.getLatLng()
                        this.lat = pos.lat
                        this.lng = pos.lng

                        this.$emit('input', {
                            lat: this.lat,
                            lng: this.lng
                        })
                    })
                }
            },

            async searchLocation(query) {
                this.isSearching = true
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
                        {
                            headers: {
                                'Accept-Language': this.$i18n?.locale || 'ar'
                            }
                        }
                    )
                    this.searchResults = await response.json()
                } catch (error) {
                    console.error('Location search error:', error)
                    this.searchResults = []
                } finally {
                    this.isSearching = false
                }
            },

            onPlaceSelected(place) {
                if (!place) return
                
                const lat = parseFloat(place.lat)
                const lng = parseFloat(place.lon)
                
                // Update local state first
                this.lat = lat
                this.lng = lng
                
                // Add marker at the exact search result coordinates
                this.addMarker({ lat, lng })
                
                // Fly to selected location with animation
                this.map.flyTo([lat, lng], 15, {
                    duration: 1
                })
                
                // Emit the exact location from search result
                this.$emit('input', { lat, lng })
            }
        }
    }
</script>

<style scoped>
    
    /* Map wrapper to create a new stacking context */
    .map-wrapper {
        position: relative;
        z-index: 0;
    }
</style>

<style>
    /* Fix leaflet container width */
    .leaflet-container {
        width: 100%;
    }
    
    /* Ensure autocomplete dropdown appears above everything */
    .v-menu__content {
        z-index: 9999 !important;
    }
</style>
